import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { DialogShell } from "@/shared/ui/DialogShell";
import { useOrgId } from "@/shared/auth/orgContext";
import { stationDirectoryQuery } from "@/features/users/api/usersQueries";
import {
  useShareMultipleEvidence,
  MAX_SHARE_RECIPIENTS,
  MAX_SHARE_MESSAGE_LENGTH,
} from "../api/shareQueries";
import { StationUser, userLabel } from "./shareUsers";

/** A video being shared, for the summary list. */
export interface ShareTarget {
  id: string;
  fileName: string;
}

interface ShareMultipleEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  /** Videos to grant access to; all get the same recipients. */
  videos: ShareTarget[];
}

/**
 * Sharing dialog for several videos at once ("Share all" over a selection):
 * pick station members and grant them read access to every listed video in one
 * action. Unlike {@link ShareEvidenceModal} there is no per-video shared-with
 * list — the existing recipients differ across videos — so this is share-only.
 */
export const ShareMultipleEvidenceModal: React.FC<
  ShareMultipleEvidenceModalProps
> = ({ open, onClose, videos }) => {
  const titleId = useId();
  const organizationId = useOrgId();
  const { user } = useAuth();

  const evidenceIds = useMemo(() => videos.map((v) => v.id), [videos]);

  // Station members for the picker (also validated server-side).
  const directory = useQuery({
    ...stationDirectoryQuery(organizationId ?? ""),
    enabled: open && !!organizationId,
  });

  const share = useShareMultipleEvidence(evidenceIds);

  const [selected, setSelected] = useState<StationUser[]>([]);
  const [message, setMessage] = useState("");

  // Fresh picker and message every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected([]);
      setMessage("");
    }
  }, [open]);

  // Candidates: station members other than the caller. Sharing with yourself is
  // moot (recipients only get read access). We can't exclude already-shared
  // members here because that set differs per video.
  const options = useMemo(() => {
    return ((directory.data?.users ?? []) as StationUser[])
      .filter((u) => u.id && u.id !== user?.id)
      .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
  }, [directory.data, user?.id]);

  const handleShare = () => {
    if (selected.length === 0 || evidenceIds.length === 0) return;
    share.mutate(
      { userIds: selected.map((u) => u.id), message },
      {
        // Keep the dialog open if any videos failed so the user can retry
        // with the same recipients and message still in place.
        onSuccess: (result) => {
          if (result.failed.length === 0) onClose();
        },
      },
    );
  };

  const videoCount = videos.length;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      closeDisabled={share.isPending}
      maxWidth={480}
      titleId={titleId}
    >
      <DialogTitle id={titleId} sx={{ pr: 6 }}>
        Share {videoCount === 1 ? "video" : `${videoCount} videos`}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.25 }}
        >
          Grant view access to other members of your station.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={options}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionDisabled={() => selected.length >= MAX_SHARE_RECIPIENTS}
            loading={directory.isLoading}
            disabled={share.isPending}
            noOptionsText={
              directory.isError
                ? "Couldn't load station members"
                : "No one to share with"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selected.length === 0 ? "Add people…" : undefined}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleShare}
            loading={share.isPending}
            disabled={selected.length === 0}
            startIcon={<PersonAddAlt1Icon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Share
          </Button>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          label="Message (optional)"
          placeholder="Add a note for the people you're sharing with…"
          value={message}
          onChange={(e) =>
            setMessage(e.target.value.slice(0, MAX_SHARE_MESSAGE_LENGTH))
          }
          disabled={share.isPending}
          inputProps={{ maxLength: MAX_SHARE_MESSAGE_LENGTH }}
          helperText={`${message.length}/${MAX_SHARE_MESSAGE_LENGTH}`}
          FormHelperTextProps={{ sx: { textAlign: "right", m: 0, mt: 0.5 } }}
          sx={{ mt: 1.5 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {videoCount === 1
            ? "Sharing video"
            : `Sharing ${videoCount} videos`}
        </Typography>
        <List dense disablePadding sx={{ maxHeight: 220, overflowY: "auto" }}>
          {videos.map((v) => (
            <ListItem key={v.id} disableGutters>
              <ListItemText
                primary={v.fileName}
                primaryTypographyProps={{
                  variant: "body2",
                  noWrap: true,
                  title: v.fileName,
                }}
              />
            </ListItem>
          ))}
        </List>

        {selected.length > 0 && (
          <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((u) => (
              <Chip key={u.id} size="small" label={userLabel(u)} />
            ))}
          </Box>
        )}
      </DialogContent>
    </DialogShell>
  );
};
